// framework/src/client/components/ai-workspace/ThreadStateStore.js

/**
 * Manages the client-side state for all loaded threads in the workspace.
 * Resolves cross-thread stream leakage and provides snappy DOM restoration on switch.
 */
export class ThreadStateStore {
    constructor({ maxSnapshots = 5 } = {}) {
        this._states = new Map();  // threadId -> ThreadState
        this._lruOrder = [];       // Least recently used order for DOM eviction
        this._maxSnaps = maxSnapshots;
    }

    get(threadId) {
        return this._states.get(threadId);
    }

    getOrCreate(threadId) {
        if (!threadId) return null;
        if (!this._states.has(threadId)) {
            this._states.set(threadId, {
                isProcessing:        false,
                streamBuffer:        '',
                cancellationNoticeShown: false,
                pendingFollowUps:    [],
                pendingPresentations: [],
                domSnapshot:         null,   // detached HTMLElement or DocumentFragment
                scrollTop:           0,
                chatListState:       null,   // preserved messages/virtualizer state
                hasNewActivity:      false,
                unreadCount:         0,
                lastMessagePreview:  null,
                agentCapabilities:   null,   // null = not loaded yet
            });
        }
        return this._states.get(threadId);
    }

    saveSnapshot(threadId, domNode, scrollTop, chatListState = null) {
        if (!threadId || !domNode) return;
        const state = this.getOrCreate(threadId);
        state.domSnapshot = domNode;
        state.scrollTop   = scrollTop;
        state.chatListState = chatListState;

        // LRU bookkeeping
        this._lruOrder = this._lruOrder.filter(id => id !== threadId);
        this._lruOrder.push(threadId);

        // Evict oldest if we exceed the limit
        if (this._lruOrder.length > this._maxSnaps) {
            const evictId = this._lruOrder.shift();
            if (this._states.has(evictId)) {
                this._states.get(evictId).domSnapshot = null;
                this._states.get(evictId).chatListState = null;
            }
        }
    }

    delete(threadId) {
        this._states.delete(threadId);
        this._lruOrder = this._lruOrder.filter(id => id !== threadId);
    }

    clear() {
        this._states.clear();
        this._lruOrder = [];
    }
}
